import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Loading } from "@/components/app/Bits";
import { RouteMeta } from "@/components/public/RouteMeta";
import { MfaGate } from "@/components/security/Mfa";
import { ErrorBoundary, StateScreen, StateButton } from "@/components/app/AppStates";
import AppShell from "@/components/app/AppShell";
import { FeatureGate } from "@/components/app/UpgradeGate";
import Landing from "@/pages/public/Landing";
const LeakCalculator = lazy(() => import("@/pages/public/LeakCalculator"));
const Pricing = lazy(() => import("@/pages/public/Pricing"));
const Training = lazy(() => import("@/pages/public/Training"));
const PlanFinder = lazy(() => import("@/pages/public/PlanFinder"));
const Checkout = lazy(() => import("@/pages/app/Checkout"));
const VendorDetail = lazy(() => import("@/pages/app/inv/VendorDetail"));
import { Login, Signup, ForgotPassword, ResetPassword, InviteAccept, AuthCallback } from "@/pages/public/Auth";
const PaymentSuccess = lazy(() => import("@/pages/app/PaymentSuccess"));
const Onboarding = lazy(() => import("@/pages/app/Onboarding"));
import Dashboard from "@/pages/app/Dashboard";
import Today from "@/pages/app/Today";
import { ManagerLogList, ManagerLogForm } from "@/pages/app/ManagerLog";
import Handoff from "@/pages/app/Handoff";
import Feed from "@/pages/app/Feed";
import { Tasks, Waste, Incidents, Employees, Documents, Beos } from "@/pages/app/Modules";
const BeoDetail = lazy(() => import("@/pages/app/BeoDetail"));
const Tips = lazy(() => import("@/pages/app/Tips"));
const TipShift = lazy(() => import("@/pages/app/tips/TipShift"));
const TipPolicy = lazy(() => import("@/pages/app/tips/TipPolicy"));
import { MySchedule } from "@/components/employee/MySchedule";
import { MyPerformance, AskCoach } from "@/components/employee/MyGrowth";
import { EmployeeShell, EmployeeHome, MyTips, MyTipDetail, MyTasks, EmployeeMore, MyChecklists, MyShiftSales } from "@/pages/app/EmployeePortal";
import { InvLayout, InvOverview } from "@/pages/app/inv/InvHome";
import { InvProducts, ProductDetail } from "@/pages/app/inv/InvProducts";
import { InvCounts, CountSheet } from "@/pages/app/inv/InvCount";
import { InvInvoices, InvoiceDetail } from "@/pages/app/inv/InvInvoices";
import { InvVariance, InvReports, InvOrders, InvVendors, InvSales } from "@/pages/app/inv/InvMore";
const InvMapping = lazy(() => import("@/pages/app/inv/InvMapping"));
const InvCounted = lazy(() => import("@/pages/app/inv/InvCounted"));
const InvSheets = lazy(() => import("@/pages/app/inv/InvSheets"));
const InvCategories = lazy(() => import("@/pages/app/inv/InvCategories"));
const InvAppSync = lazy(() => import("@/pages/app/inv/InvAppSync"));
const InvAppAccess = lazy(() => import("@/pages/app/inv/InvAppAccess"));
const InvAdminSettings = lazy(() => import("@/pages/app/inv/InvAdminSettings"));
const InvSessionReview = lazy(() => import("@/pages/app/inv/InvSessionReview"));
const InvTasks = lazy(() => import("@/pages/app/inv/InvTasks"));
import { InvRecipes, RecipeEditor } from "@/components/recipes/RecipeBuilder";
import Checklists from "@/pages/app/Checklists";
const Sales = lazy(() => import("@/pages/app/Sales"));
const EmployeeProfile = lazy(() => import("@/pages/app/EmployeeProfile"));
const PeopleSettings = lazy(() => import("@/pages/app/PeopleSettings"));
const Access = lazy(() => import("@/pages/app/Access"));
const Inbox = lazy(() => import("@/pages/app/Inbox"));
const PortalExtras = import("@/components/employee/PortalExtras");
const MySettings = lazy(() => PortalExtras.then((m) => ({ default: m.MySettings })));
const MyRecipes = lazy(() => PortalExtras.then((m) => ({ default: m.MyRecipes })));
const MyMessages = lazy(() => PortalExtras.then((m) => ({ default: m.MyMessages })));
const DuplicateReview = lazy(() => import("@/pages/app/inv/DuplicateReview"));
const Integrations = lazy(() => import("@/pages/app/Integrations"));
const Guests = lazy(() => import("@/pages/app/Guests"));
const ProductMix = lazy(() => import("@/pages/app/ProductMix"));
const Schedule = lazy(() => import("@/pages/app/Schedule"));
const TeamSales = lazy(() => import("@/pages/app/TeamSales"));
const TipReports = lazy(() => import("@/pages/app/TipReports"));
const UnlinkedData = lazy(() => import("@/pages/app/UnlinkedData"));
const NightlyClose = lazy(() => import("@/pages/app/NightlyClose"));
const ShiftClose = lazy(() => import("@/pages/app/ShiftClose"));
const ShiftCloseStart = lazy(() => import("@/pages/app/ShiftClose").then((m) => ({ default: m.ShiftCloseStart })));
const AlertSettings = lazy(() => import("@/pages/app/AlertSettings"));
const MyNotifications = lazy(() => import("@/pages/app/MyNotifications"));
const DataCorrections = lazy(() => import("@/pages/app/DataCorrections"));
const WhatIs86d = lazy(() => import("@/pages/public/WhatIs86d"));
const Features = lazy(() => import("@/pages/public/Features"));
const FeatureGuide = lazy(() => import("@/pages/public/FeatureGuide"));
const Resources = lazy(() => import("@/pages/public/Resources"));
const Article = lazy(() => import("@/pages/public/Article"));
const Compare = lazy(() => import("@/pages/public/Compare"));
const CompareVendor = lazy(() => import("@/pages/public/CompareVendor"));
const SalesSetup = lazy(() => import("@/pages/app/SalesSetup"));
const EmployeeImport = lazy(() => import("@/pages/app/EmployeeImport"));
const EmployeeSales = lazy(() => import("@/pages/app/EmployeeSales"));
const Leaks = lazy(() => import("@/pages/app/Leaks"));
const WeeklyReport = lazy(() => import("@/pages/app/WeeklyReport"));
const Reports = lazy(() => import("@/pages/app/Reports"));
const Group = lazy(() => import("@/pages/app/Group"));
const Builder = lazy(() => import("@/pages/app/Builder"));
const Team = lazy(() => import("@/pages/app/Team"));
const TeamOnboarding = lazy(() => import("@/pages/app/TeamOnboarding"));
const MyOnboardingPage = lazy(() => import("@/components/onboarding/MyOnboarding").then((m) => ({ default: m.MyOnboarding })));
const Billing = lazy(() => import("@/pages/app/Billing"));
import More from "@/pages/app/More";
const Admin = lazy(() => import("@/pages/admin/Admin"));
const SecurityCenter = lazy(() => import("@/pages/app/SecurityCenter"));
const LegalCenter = lazy(() => import("@/pages/app/LegalCenter"));
const Trust = lazy(() => import("@/pages/public/Trust"));
const PrivacyNotice = lazy(() => import("@/pages/public/PrivacyNotice"));
const Contact = lazy(() => import("@/pages/public/Contact"));
const Support = lazy(() => import("@/pages/public/Support"));
const VerifyEmail = lazy(() => import("@/pages/public/VerifyEmail"));
const OAuthConsent = lazy(() => import("@/pages/public/OAuthConsent"));
const MobileApp = lazy(() => import("@/pages/app/MobileApp"));
const PlatformLogin = lazy(() => import("@/pages/platform/PlatformLogin"));
const PlatformShell = lazy(() => import("@/pages/platform/PlatformShell"));
const PADashboard = lazy(() => import("@/pages/platform/PAOverview"));
const PARestaurants = lazy(() => import("@/pages/platform/PARestaurants"));
const PABilling = lazy(() => import("@/pages/platform/PABilling"));
const PASupport = lazy(() => import("@/pages/platform/PASupport"));
const PAMarketing = lazy(() => import("@/pages/platform/PAMarketing"));
const PASystemHealth = lazy(() => import("@/pages/platform/PASystemHealth"));
const PASettings = lazy(() => import("@/pages/platform/PASettings"));
const HelpCenter = lazy(() => import("@/pages/app/HelpCenter"));
const PAAccount = lazy(() => import("@/pages/platform/PAAccount"));
const Guide = lazy(() => import("@/pages/public/Guides"));
const Unsubscribe = lazy(() => import("@/pages/public/Guides").then((m) => ({ default: m.Unsubscribe })));
const Discover = lazy(() => import("@/pages/public/Discover"));
const RestaurantPublic = lazy(() => import("@/pages/public/RestaurantPublic"));
const GuestAccount = lazy(() => import("@/pages/public/GuestAccount"));
const BookingManage = lazy(() => import("@/pages/public/GuestAccount").then((m) => ({ default: m.BookingManage })));
const MarketingNetwork = lazy(() => import("@/pages/app/MarketingNetwork"));
const ForRestaurants = lazy(() => import("@/pages/app/MarketingNetwork").then((m) => ({ default: m.ForRestaurants })));
const PASecurity = lazy(() => import("@/pages/platform/PASecurity"));
const PACommercial = lazy(() => import("@/pages/admin/Admin").then((m) => ({ default: m.AdminConsoleTabs })));

const AccountSuspended = ({ status }) => (
  <div className="min-h-screen flex items-center justify-center p-6 bg-[#FBF9F5]" data-testid="account-suspended">
    <div className="max-w-md text-center"><div className="font-display text-4xl mb-3">86'd</div>
      <h1 className="font-display text-2xl mb-2">This account is {status === "closed" ? "closed" : "suspended"}</h1>
      <p className="text-stone-600 text-sm">Access is paused for everyone on this restaurant account. Please contact 86'd support to restore access.</p></div>
  </div>
);

const ISSUES = {
  no_restaurant: ["membership", "Your account isn't connected to a restaurant", "We found your sign-in, but the restaurant it belongs to is missing or was closed. Nothing has been changed — contact 86'd support so we can reconnect it safely."],
  no_location_access: ["membership", "No locations assigned yet", "Your account is active but hasn't been given access to any location. Ask your owner or GM to assign you in Team Members."],
  role_invalid: ["setup", "Your account setup needs attention", "Your account's role couldn't be recognized, so access is paused to keep your restaurant's data safe. Contact your owner or 86'd support."],
};

function SignOutBtn() {
  const { logout } = useAuth();
  return <StateButton onClick={async () => { await logout(); window.location.href = "/login"; }} data-testid="state-signout">Sign out</StateButton>;
}

function Protected({ children, admin }) {
  const { session, authError, expired, reload, user, org } = useAuth();
  const loc = useLocation();
  if (authError) return (
    <StateScreen kind="unavailable" testid="service-unavailable" title="86'd is temporarily unavailable" refId={authError.ref}
      text="We couldn't confirm your session because the service didn't respond. Your data is safe — try again in a moment."
      actions={<StateButton primary onClick={reload} data-testid="service-retry">Try again</StateButton>} />);
  if (session === null) return <div className="min-h-screen flex items-center justify-center" data-testid="checking-session"><Loading label="Checking your session…" /></div>;
  if (!session) return <Navigate to="/login" replace state={{ from: loc.pathname + loc.search, expired }} />;
  if (!user) return (
    <StateScreen kind="setup" testid="account-attention" title="Sign-in didn't finish" text="Your sign-in is missing a step (such as two-step verification). Sign in again to continue."
      actions={<SignOutBtn />} />);
  if (admin) return user.role === "superadmin" ? children : <Navigate to="/app/dashboard" replace />;
  if (user.role === "superadmin") return <Navigate to="/admin" replace />;
  if (session.account_issue && ISSUES[session.account_issue]) {
    const [kind, title, text] = ISSUES[session.account_issue];
    return <StateScreen kind={kind} testid="account-attention" title={title} text={text} actions={<SignOutBtn />} />;
  }
  if (["suspended", "closed"].includes(session.state?.platform_status)) return <AccountSuspended status={session.state.platform_status} />;
  if (session.mfa_setup_required) return <MfaGate />;
  if (user.role === "employee" && !loc.pathname.startsWith("/app/me")) return <Navigate to="/app/me" replace />;
  if (user.role !== "employee" && loc.pathname.startsWith("/app/me")) return <Navigate to="/app/dashboard" replace />;
  if (org && !org.onboarding_complete && user.role === "owner" && loc.pathname !== "/onboarding") return <Navigate to="/onboarding" replace />;
  return children;
}

function Router() {
  const loc = useLocation();
  if (loc.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <>
    <RouteMeta />
    <Suspense fallback={<Loading />}>
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/profit-leak-calculator" element={<LeakCalculator />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/what-is-86d" element={<WhatIs86d />} />
      <Route path="/features" element={<Features />} />
      <Route path="/features/:slug" element={<FeatureGuide />} />
      <Route path="/resources" element={<Resources />} />
      <Route path="/resources/:slug" element={<Article />} />
      <Route path="/compare" element={<Compare />} />
      <Route path="/compare/:slug" element={<CompareVendor />} />
      <Route path="/security" element={<Trust />} />
      <Route path="/privacy" element={<PrivacyNotice />} />
      <Route path="/discover" element={<Discover />} />
      <Route path="/discover/r/:slug" element={<RestaurantPublic />} />
      <Route path="/discover/account" element={<GuestAccount />} />
      <Route path="/discover/booking/:ref" element={<BookingManage />} />
      <Route path="/discover/guides/:slug" element={<Guide />} />
      <Route path="/discover/unsubscribe" element={<Unsubscribe />} />
      <Route path="/for-restaurants" element={<ForRestaurants />} />
      <Route path="/legal/:key" element={<PrivacyNotice />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/support" element={<Support />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/oauth/consent" element={<OAuthConsent />} />
      <Route path="/training" element={<Training />} />
      <Route path="/find-your-plan" element={<PlanFinder />} />
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/invite/:token" element={<InviteAccept />} />
      <Route path="/payment/success" element={<Protected><PaymentSuccess /></Protected>} />
      <Route path="/onboarding" element={<Protected><Onboarding /></Protected>} />
      <Route path="/admin" element={<Protected admin><Admin /></Protected>} />
      <Route path="/platform-admin/login" element={<PlatformLogin />} />
      <Route path="/platform-admin/invite/:token" element={<PlatformLogin />} />
      <Route path="/platform-admin" element={<PlatformShell />}>
        <Route index element={<PADashboard />} />
        <Route path="restaurants" element={<PARestaurants />} />
        <Route path="billing" element={<PABilling />} />
        <Route path="support" element={<PASupport />} />
        <Route path="marketing" element={<PAMarketing />} />
        <Route path="system" element={<PASystemHealth />} />
        <Route path="settings" element={<PASettings />} />
        <Route path="accounts" element={<Navigate to="/platform-admin/restaurants" replace />} />
        <Route path="accounts/:id" element={<PAAccount />} />
        <Route path="cases" element={<Navigate to="/platform-admin/support" replace />} />
        <Route path="audit" element={<Navigate to="/platform-admin/settings?tab=audit" replace />} />
        <Route path="listings" element={<Navigate to="/platform-admin/marketing?tab=listings" replace />} />
        <Route path="guides" element={<Navigate to="/platform-admin/marketing?tab=agent" replace />} />
        <Route path="email" element={<Navigate to="/platform-admin/system?tab=email" replace />} />
        <Route path="admins" element={<Navigate to="/platform-admin/settings?tab=admins" replace />} />
        <Route path="security" element={<PASecurity />} />
        <Route path="commercial" element={<div data-testid="pa-commercial"><h1 className="font-display text-4xl mb-5">Commercial</h1><PACommercial /></div>} />
      </Route>
      <Route path="/app/me" element={<Protected><EmployeeShell /></Protected>}>
        <Route index element={<EmployeeHome />} />
        <Route path="tips" element={<MyTips />} />
        <Route path="tips/:id" element={<MyTipDetail />} />
        <Route path="tasks" element={<MyTasks />} />
        <Route path="checklists" element={<MyChecklists />} />
        <Route path="shift-sales" element={<MyShiftSales />} />
        <Route path="checklists/:id" element={<MyChecklists />} />
        <Route path="growth" element={<MyPerformance />} />
        <Route path="schedule" element={<MySchedule />} />
        <Route path="ask" element={<AskCoach />} />
        <Route path="more" element={<EmployeeMore />} />
        <Route path="settings" element={<Suspense fallback={<Loading />}><MySettings /></Suspense>} />
        <Route path="recipes" element={<Suspense fallback={<Loading />}><MyRecipes /></Suspense>} />
        <Route path="messages" element={<Suspense fallback={<Loading />}><MyMessages /></Suspense>} />
        <Route path="onboarding" element={<Suspense fallback={<Loading />}><MyOnboardingPage /></Suspense>} />
      </Route>
      <Route path="/app" element={<Protected><AppShell /></Protected>}>
        <Route index element={<Navigate to="dashboard" replace />} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="today" element={<Today />} />
        <Route path="log" element={<ManagerLogList />} />
        <Route path="log/new" element={<ManagerLogForm />} />
        <Route path="log/:id" element={<ManagerLogForm />} />
        <Route path="handoff" element={<Handoff />} />
        <Route path="feed" element={<Feed />} />
        <Route path="tasks" element={<Tasks />} />
        <Route path="checklists" element={<Checklists />} />
        <Route path="incidents" element={<Incidents />} />
        <Route path="waste" element={<Waste />} />
        <Route path="beo" element={<Beos />} />
        <Route path="beo/:id" element={<BeoDetail />} />
        <Route path="inventory" element={<FeatureGate feature="inventory"><InvLayout /></FeatureGate>}>
          <Route index element={<InvOverview />} />
          <Route path="count" element={<InvCounts />} />
          <Route path="count/:id" element={<CountSheet />} />
          <Route path="count/:id/review" element={<InvSessionReview />} />
          <Route path="counted" element={<InvCounted />} />
          <Route path="sheets" element={<InvSheets />} />
          <Route path="categories" element={<InvCategories />} />
          <Route path="app-sync" element={<InvAppSync />} />
          <Route path="app-access/:section" element={<InvAppAccess />} />
          <Route path="settings" element={<InvAdminSettings />} />
          <Route path="actions" element={<InvTasks />} />
          <Route path="products" element={<InvProducts />} />
          <Route path="products/:id" element={<ProductDetail />} />
          <Route path="recipes" element={<InvRecipes />} />
          <Route path="recipes/:id" element={<RecipeEditor />} />
          <Route path="invoices" element={<InvInvoices />} />
          <Route path="invoices/:id" element={<InvoiceDetail />} />
          <Route path="orders" element={<InvOrders />} />
          <Route path="vendors" element={<InvVendors />} />
          <Route path="vendors/:id" element={<VendorDetail />} />
          <Route path="duplicates" element={<DuplicateReview />} />
          <Route path="variance" element={<InvVariance />} />
          <Route path="mapping" element={<InvMapping />} />
          <Route path="reports" element={<InvReports />} />
          <Route path="sales" element={<InvSales />} />
        </Route>
        <Route path="tips" element={<FeatureGate feature="tips"><Tips /></FeatureGate>} />
        <Route path="tips/shift/:id" element={<FeatureGate feature="tips"><TipShift /></FeatureGate>} />
        <Route path="tips/reports" element={<FeatureGate feature="tips"><TipReports /></FeatureGate>} />
        <Route path="tips/nightly" element={<FeatureGate feature="tips"><NightlyClose /></FeatureGate>} />
        <Route path="close" element={<ShiftCloseStart />} />
        <Route path="close/:id" element={<ShiftClose />} />
        <Route path="alerts" element={<AlertSettings />} />
        <Route path="notifications" element={<MyNotifications />} />
        <Route path="settings/sales-setup" element={<SalesSetup />} />
        <Route path="marketing" element={<MarketingNetwork />} />
        <Route path="people/import" element={<EmployeeImport />} />
        <Route path="settings/tips" element={<TipPolicy />} />
        <Route path="sales" element={<Sales />} />
        <Route path="data-corrections" element={<DataCorrections />} />
        <Route path="team-sales" element={<TeamSales />} />
        <Route path="team-sales/:id" element={<EmployeeSales />} />
        <Route path="leaks" element={<Leaks />} />
        <Route path="report" element={<WeeklyReport />} />
        <Route path="reports" element={<Reports />} />
        <Route path="group" element={<Group />} />
        <Route path="employees" element={<Employees />} />
        <Route path="schedule" element={<Schedule />} />
        <Route path="integrations" element={<Integrations />} />
        <Route path="security" element={<SecurityCenter />} />
        <Route path="legal" element={<LegalCenter />} />
        <Route path="guests" element={<Guests />} />
        <Route path="product-mix" element={<ProductMix />} />
        <Route path="people/settings" element={<PeopleSettings />} />
        <Route path="people/unlinked" element={<UnlinkedData />} />
        <Route path="access" element={<Access />} />
        <Route path="inbox" element={<Inbox />} />
        <Route path="recipes" element={<Suspense fallback={<Loading />}><MyRecipes /></Suspense>} />
        <Route path="people/:id" element={<EmployeeProfile />} />
        <Route path="documents" element={<Documents />} />
        <Route path="team" element={<Team />} />
        <Route path="onboarding" element={<TeamOnboarding />} />
        <Route path="settings/mobile-app" element={<MobileApp />} />
        <Route path="settings" element={<Builder />} />
        <Route path="settings/:section" element={<Builder />} />
        <Route path="billing" element={<Billing />} />
        <Route path="help" element={<HelpCenter />} />
        <Route path="checkout" element={<Checkout />} />
        <Route path="more" element={<More />} />
      </Route>
      <Route path="*" element={<div className="min-h-screen flex flex-col items-center justify-center gap-3" data-testid="not-found"><div className="font-display text-5xl">86'd</div><p className="text-stone-600">This page has been 86'd.</p><a href="/" className="underline">Go home</a></div>} />
    </Routes>
    </Suspense>
    </>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ErrorBoundary>
          <Router />
        </ErrorBoundary>
        <Toaster position="top-center" richColors closeButton />
      </AuthProvider>
    </BrowserRouter>
  );
}
